import type { Draft, PayloadAction } from '@reduxjs/toolkit';
import { createSlice, current, isDraft } from '@reduxjs/toolkit';
import { message } from '@repo/ui';
import { LocalStorage, RandomUtils } from '@vectord/utils';
import type { ComparatorType } from 'advanced-filters/data/comparators';
import type { BaseOperatorType, BaseOptionsType } from 'advanced-filters/data/options';
import type { CriteriaItem, FilterLevel } from 'advanced-filters/types/filter';
import { validatePendingFilter } from 'advanced-filters/utils/validateCondition';

export type TimeFilterOptionsType = {
  filterType: ComparatorType;
  offsetValue?: string;
  fromTime?: string;
  toTime?: string;
};

export type KeyValueType = {
  key?: string;
  value: any;
};

export interface FilterForm {
  id: string;
  conditionKey: BaseOptionsType;
  conditionComparator: ComparatorType;
  conditionValue?: KeyValueType;
  subConditionKey?: any;
  subConditionComparator?: ComparatorType;
  subConditionValue?: KeyValueType;
  timeFilter?: TimeFilterOptionsType;
  type?: 'criteria';
}

export interface FilterStateType {
  // Keep existing fields for backward compatibility
  comparator: BaseOperatorType;
  filters: FilterForm[];
  pendingFilter: Partial<FilterForm>;
  appliedFilters: {
    comparator: BaseOperatorType;
    levels: FilterLevel[];
  };
  pendingFilterError?: string | null;
  fetchError?: string | null;

  // New fields for levels support
  levels?: FilterLevel[];
  selectedLevel?: number;
}

export const defaultState: FilterStateType = {
  // Existing fields
  comparator: 'OR',

  // @deprecated Use levels instead
  filters: [],

  appliedFilters: {
    comparator: 'OR',
    levels: [],
  },
  pendingFilter: {},
  pendingFilterError: null,
  fetchError: null,

  // New fields
  levels: [{
    type: 'level',
    level: 0,
    operator: 'OR',
    criteria: [],
  }],
  selectedLevel: 0,
};

export interface StateType {
  selectedEntity?: string;
  allEntities: Record<string, FilterStateType>;
}

const initialState: StateType = {
  allEntities: {},
};

const getSelectedEntityState = (state: Draft<StateType>): Draft<FilterStateType> | null => {
  const { selectedEntity } = state;
  if (!selectedEntity) {
    throw new Error('Selected Entity not found, use SelectedEntityProvider to set the selected entity');
  }

  if (!state.allEntities[selectedEntity]) return null;
  return state.allEntities[selectedEntity];
};

const lsInstance = LocalStorage.getInstance('store.advancedFilters');
const refreshStateLsInstance = LocalStorage.getInstance('store.advancedFilters.refreshState');

// Utility function to convert from old format to levels format
const filtersToLevels = (filters: FilterForm[], comparator: BaseOperatorType): FilterLevel[] => [{
  type: 'level',
  level: 0,
  operator: comparator,
  criteria: filters.map((filter) => ({ ...filter, type: 'criteria' })),
}];
// Helper function to store levels for an entity
const storeLevels = (entityKey: string, levels: FilterLevel[], comparator: BaseOperatorType) => {
  const storedData = lsInstance?.config || {};

  const parsedLevels = isDraft(levels) ? current(levels) : levels;

  // Check if there's existing data in the original format
  const existingData = storedData[entityKey];

  if (existingData && 'filters' in existingData && Array.isArray(existingData.filters)) {
    const isRefreshed = refreshStateLsInstance?.[entityKey];
    // Convert from original format to new format
    const convertedLevels = filtersToLevels(existingData.filters, existingData.comparator || comparator);

    const finalData: Record<string, any> = {
      ...(storedData as Record<string, any>),
      [entityKey]: {
        levels: parsedLevels.length ? parsedLevels : convertedLevels,
        comparator,
      },
    };

    lsInstance.config = finalData;
    if (!isRefreshed) {
      refreshStateLsInstance[entityKey] = true;
      window.location.reload();
    } else {
      message.error('We are having some trouble loading the page, Please refresh the page');
    }
  } else {
    // Store in the new format directly
    const finalData: Record<string, any> = {
      ...(storedData as Record<string, any>),
      [entityKey]: {
        levels: parsedLevels.length ? parsedLevels : structuredClone(defaultState.levels),
        comparator,
      },
    };

    lsInstance.config = finalData;
  }
};

export const filterSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    setSelectedEntity: (state, action: PayloadAction<string | undefined>) => {
      if (state.selectedEntity === action.payload) return;

      state.selectedEntity = action.payload;

      if (action.payload && !state.allEntities[action.payload]) {
        state.allEntities[action.payload] = structuredClone(defaultState);
      } else if (!action.payload) {
        state.selectedEntity = undefined;
      }
    },
    setFilterOperator: (
      state,
      action: PayloadAction<BaseOperatorType | { level: number; operator: BaseOperatorType }>,
    ) => {
      const entityState = getSelectedEntityState(state);
      if (!entityState) return;

      if (typeof action.payload === 'string') {
        // Any/All buckets toggle (base comparator)
        entityState.comparator = action.payload;
      } else {
        const { level, operator } = action.payload;
        const { levels } = entityState;
        if (!levels?.length) return;

        const bucket = levels.find(
          (item): item is CriteriaItem & { type: 'level' } => item.type === 'level' && item.level === level,
        );
        if (bucket) {
          bucket.operator = operator;
        }
      }
    },
    modifyPendingFilter: (state, action: PayloadAction<Partial<FilterForm>>) => {
      const entityState = getSelectedEntityState(state);
      if (!entityState) return;

      entityState.pendingFilter = {
        ...entityState.pendingFilter,
        ...action.payload,
      };
      if (!entityState.pendingFilter.id) {
        entityState.pendingFilter.id = RandomUtils.generateUUID();
      }
      // Remove error when user makes a change
      entityState.pendingFilterError = null;
    },
    resetPendingFilter: (state) => {
      const entityState = getSelectedEntityState(state);
      if (!entityState) return;

      entityState.pendingFilter = {};
    },
    savePendingFilter: (state) => {
      const entityState = getSelectedEntityState(state);
      if (!entityState) return;

      const { levels, selectedLevel } = entityState;
      if (!levels?.length) return;

      // Find the bucket in the first level's criteria
      const bucket = levels.find(
        (item): item is CriteriaItem & { type: 'level' } => item.type === 'level' && item.level === selectedLevel,
      );
      if (!bucket) return;

      const { error, filter } = validatePendingFilter(
        entityState.pendingFilter,
        bucket.criteria.filter((item): item is FilterForm => item.type !== 'level'),
      );

      if (error) {
        entityState.pendingFilterError = error;
        return;
      }

      if (!filter) return;

      const existingIndex = bucket.criteria.findIndex((f) => 'id' in f && f.id === filter.id);
      if (existingIndex !== -1) {
        bucket.criteria[existingIndex] = { ...filter, type: 'criteria' };
      } else {
        bucket.criteria.push({ ...filter, type: 'criteria' });
      }

      entityState.pendingFilter = {};
      entityState.pendingFilterError = null;
      message.success('Filter added successfully');
    },
    setFetchError: (state, action: PayloadAction<string | null>) => {
      const entityState = getSelectedEntityState(state);
      if (!entityState) return;

      entityState.fetchError = action.payload;
    },
    deleteFilter: (state, action: PayloadAction<number>) => {
      const entityState = getSelectedEntityState(state);
      if (!entityState) return;

      const { levels, selectedLevel } = entityState;
      if (!levels?.length) return;

      const bucket = levels.find(
        (item): item is CriteriaItem & { type: 'level' } => item.type === 'level' && item.level === selectedLevel,
      );
      if (!bucket) return;

      bucket.criteria.splice(action.payload, 1);
    },
    editFilter: (state, action: PayloadAction<number>) => {
      const entityState = getSelectedEntityState(state);
      if (!entityState) return;

      const { levels, selectedLevel } = entityState;
      if (!levels?.length) return;

      const bucket = levels.find(
        (item): item is CriteriaItem & { type: 'level' } => item.type === 'level' && item.level === selectedLevel,
      );
      if (!bucket) return;

      const filterToEdit = bucket.criteria[action.payload];
      if (!filterToEdit || filterToEdit.type === 'level') return;

      const { type, ...rest } = filterToEdit;
      entityState.pendingFilter = rest;
    },
    applyFilter: (state) => {
      const entityState = getSelectedEntityState(state);
      if (!entityState || !state.selectedEntity) return;

      const { levels } = entityState;
      if (!levels?.length) return;

      entityState.appliedFilters.levels = entityState.levels || [];
      entityState.appliedFilters.comparator = entityState.comparator || 'AND';

      storeLevels(state.selectedEntity, entityState.levels || [], entityState.comparator);
    },
    clearFilter: (state) => {
      const entityState = getSelectedEntityState(state);
      if (!entityState || !state.selectedEntity) return;

      state.allEntities[state.selectedEntity] = structuredClone(defaultState);
      storeLevels(state.selectedEntity, [], entityState.comparator);
    },
    setFilter: (
      state,
      action: PayloadAction<{ levels: FilterLevel[]; comparator: BaseOperatorType; }>,
    ) => {
      const entityState = getSelectedEntityState(state);

      if (!entityState || !state.selectedEntity) return;

      entityState.levels = action.payload.levels;
      entityState.comparator = action.payload.comparator;
      entityState.appliedFilters.levels = action.payload.levels;
      entityState.appliedFilters.comparator = action.payload.comparator;

      storeLevels(state.selectedEntity, entityState.levels || [], entityState.comparator);
    },
    setSelectedLevel: (state, action: PayloadAction<number>) => {
      const entityState = getSelectedEntityState(state);
      if (!entityState) return;

      entityState.selectedLevel = action.payload;
    },
    addFilterLevel: (state) => {
      const entityState = getSelectedEntityState(state);
      if (!entityState) return;

      if (!entityState.levels?.length) {
        entityState.levels = [{
          type: 'level',
          level: 0,
          operator: entityState.comparator,
          criteria: [],
        }];
        return;
      }

      const newLevel = entityState.levels.filter(
        (item): item is CriteriaItem & { type: 'level' } => item.type === 'level',
      ).length;

      entityState.levels.push({
        type: 'level',
        level: newLevel,
        operator: 'AND',
        criteria: [],
      });
      entityState.selectedLevel = newLevel;
    },
    deleteBucket: (state, action: PayloadAction<{ level: number }>) => {
      const entityState = getSelectedEntityState(state);
      if (!entityState) return;

      const { levels } = entityState;
      if (!levels?.length) return;

      // Store the top-level operator before deletion
      const topLevelOperator = entityState.comparator;

      // Remove the bucket with the specified level
      entityState.levels = levels.filter(
        (item) => !(item.type === 'level' && item.level === action.payload.level),
      );

      // Update the levels of remaining buckets
      let newLevel = 0;
      entityState.levels.forEach((item) => {
        if (item.type === 'level') {
          item.level = newLevel;
          newLevel += 1;
        }
      });

      // Ensure the top-level operator is preserved
      entityState.comparator = topLevelOperator;

      // Update selected level if it was deleted
      const currentSelectedLevel = entityState.selectedLevel ?? 0;
      if (currentSelectedLevel === action.payload.level) {
        entityState.selectedLevel = Math.max(0, newLevel - 1);
      } else if (currentSelectedLevel > action.payload.level) {
        entityState.selectedLevel = currentSelectedLevel - 1;
      }
    },
  },
});

export const {
  setSelectedEntity,
  deleteFilter,
  editFilter,
  modifyPendingFilter,
  resetPendingFilter,
  savePendingFilter,
  setFilterOperator,
  setFetchError,
  applyFilter,
  clearFilter,
  setFilter,
  setSelectedLevel,
  addFilterLevel,
  deleteBucket,
} = filterSlice.actions;

export default filterSlice.reducer;
